import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, Loader2, FileBadge, Printer, History } from "lucide-react";

const VERDICT_STYLE = {
  PASS: "bg-emerald-600 text-white",
  WARNING: "bg-amber-500 text-white",
  FAIL: "bg-red-600 text-white",
};
const STATUS_PILL = (s) => ({
  PASS: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "PASS-FORWARD": "bg-sky-100 text-sky-800 border-sky-200",
  INFO: "bg-slate-100 text-slate-600 border-slate-200",
  FAIL: "bg-red-100 text-red-800 border-red-200",
}[s] || "bg-slate-100 text-slate-600 border-slate-200");

const fmtDT = (iso) => (iso || "").replace("T", " ").slice(0, 19) + " UTC";

/* ── human-readable drill-down rendering (was a raw JSON dump) ── */
const COUNT_RE = /(count|rows|entries|batches|groups|types|lines|items|assets|drift|missing|unposted|checked|excluded)/i;
const prettyKey = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtVal = (k, v) => {
  if (typeof v === "boolean") return v ? "✓ Yes" : "✗ No";
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") {
    if (Number.isInteger(v) && COUNT_RE.test(k)) return String(v);
    return formatINR(v);
  }
  return String(v);
};

function KVTable({ obj }) {
  const scalars = Object.entries(obj).filter(([k, v]) =>
    k !== "status" && (typeof v !== "object" || v === null));
  if (!scalars.length) return null;
  return (
    <table className="w-full text-xs border border-slate-200">
      <tbody>
        {scalars.map(([k, v]) => (
          <tr key={k} className="border-b border-slate-100 last:border-0">
            <td className="px-3 py-1.5 text-slate-500 w-1/2">{prettyKey(k)}</td>
            <td className={`px-3 py-1.5 text-right tabular font-semibold ${
              typeof v === "boolean" ? (v ? "text-emerald-700" : "text-red-700") : "text-slate-800"}`}>
              {fmtVal(k, v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RowsTable({ rows }) {
  const cols = Object.keys(rows[0] || {});
  const shown = rows.slice(0, 60);
  return (
    <div className="border border-slate-200 overflow-x-auto max-h-64 overflow-y-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="bg-slate-50 sticky top-0">
            {cols.map((c) => (
              <th key={c} className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500 whitespace-nowrap">{prettyKey(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={i} className={`border-t border-slate-100 ${r.ok === false ? "bg-red-50/60" : ""}`}>
              {cols.map((c) => (
                <td key={c} className={`px-2 py-1 whitespace-nowrap ${typeof r[c] === "number" ? "text-right tabular" : ""}`}>
                  {fmtVal(c, r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > shown.length && (
        <div className="px-2 py-1 text-[10px] text-slate-400 bg-slate-50">Showing {shown.length} of {rows.length} rows</div>
      )}
    </div>
  );
}

function CheckDetail({ check }) {
  const nested = Object.entries(check).filter(([, v]) => v && typeof v === "object");
  return (
    <div className="space-y-4" data-testid="fi-drill-readable">
      <div className="flex items-center gap-2">
        <span className={`px-2.5 py-1 text-[11px] font-bold border ${STATUS_PILL(check.status)}`}>{check.status}</span>
        {check.note && <span className="text-[11px] text-slate-500 italic">{check.note}</span>}
      </div>
      <KVTable obj={check} />
      {nested.map(([k, v]) => (
        <div key={k}>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">{prettyKey(k)}</div>
          {Array.isArray(v)
            ? (v.length
              ? (typeof v[0] === "object" ? <RowsTable rows={v} /> : <div className="text-xs text-slate-700">{v.map(String).join(", ")}</div>)
              : <div className="text-xs text-slate-400">No rows — nothing outstanding</div>)
            : <KVTable obj={v} />}
        </div>
      ))}
      <details className="border border-slate-200">
        <summary className="px-3 py-2 text-[11px] text-slate-500 cursor-pointer select-none">Raw data (technical)</summary>
        <pre className="text-[10px] bg-slate-50 border-t p-3 overflow-auto whitespace-pre-wrap max-h-60">{JSON.stringify(check, null, 2)}</pre>
      </details>
    </div>
  );
}

export default function FinancialIntegrityPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [cert, setCert] = useState(null);
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [drill, setDrill] = useState(null);
  const [tab, setTab] = useState("checks");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/financial-integrity/checks");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to run integrity checks");
    } finally {
      setLoading(false);
    }
  };
  const loadHistory = async () => {
    try {
      const r = await api.get("/financial-integrity/certificates");
      setHistory(r.data);
    } catch { /* empty */ }
  };
  useEffect(() => { load(); loadHistory(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/financial-integrity/certificate");
      setCert(r.data);
      toast.success(`Certificate ${r.data.certificate_number} generated`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Certificate generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const openCert = async (id) => {
    try {
      const r = await api.get(`/financial-integrity/certificates/${id}`);
      setCert(r.data);
    } catch { toast.error("Failed to load certificate"); }
  };

  if (!data) {
    return <div className="text-slate-500 p-6" data-testid="fi-loading">{loading ? "Running integrity checks…" : "Integrity checks unavailable"}</div>;
  }

  const report = data.report;
  const labels = data.check_labels || {};
  const checkKeys = Object.keys(labels);
  const out = report.outstanding_items || {};

  return (
    <div data-testid="financial-integrity-page">
      <PageHeader
        eyebrow="Statutory Reporting"
        title="Financial Integrity Certificate"
        subtitle="10-point accounting verification over the verified General Ledger — Trial Balance, double-entry, subsidiary-ledger and statement reconciliations."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { load(); loadHistory(); }} disabled={loading} data-testid="fi-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Re-run Checks
            </Button>
            <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={generate} disabled={generating} data-testid="fi-generate-cert-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileBadge className="w-4 h-4 mr-1" />}
              Generate Certificate
            </Button>
          </div>
        }
      />

      <div className={`p-5 mb-6 flex items-center justify-between ${VERDICT_STYLE[data.verdict] || "bg-slate-700 text-white"}`} data-testid="fi-verdict-banner">
        <div className="flex items-center gap-3">
          <ShieldCheck size={28} />
          <div>
            <div className="font-display text-2xl font-black" data-testid="fi-verdict">{data.verdict}</div>
            <div className="text-xs opacity-90">
              {data.verdict === "PASS" && "All checks pass — no outstanding exceptions."}
              {data.verdict === "WARNING" && "All live-book checks PASS. Agreed-deferred items pending the Opening Balance migration are quantified below."}
              {data.verdict === "FAIL" && "One or more verification checks failed — drill into the failing checks below."}
            </div>
          </div>
        </div>
        <div className="text-right text-xs opacity-90">
          <div>Active FY: <b>{report.active_fy}</b></div>
          <div>Live Books: Dr {formatINR(report["2_debits_equal_credits"]?.live_debit)} = Cr {formatINR(report["2_debits_equal_credits"]?.live_credit)}</div>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <Button variant={tab === "checks" ? "default" : "outline"} size="sm" onClick={() => setTab("checks")} data-testid="fi-tab-checks">Checks</Button>
        <Button variant={tab === "history" ? "default" : "outline"} size="sm" onClick={() => setTab("history")} data-testid="fi-tab-history">
          <History className="w-4 h-4 mr-1" /> Certificate History ({history.total})
        </Button>
      </div>

      {tab === "checks" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {checkKeys.map(k => {
              const c = report[k] || {};
              return (
                <Card key={k} className="p-4 cursor-pointer hover:border-orange-300" onClick={() => setDrill({ key: k, check: c })} data-testid={`fi-check-${k}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm">{labels[k]}</div>
                    <span className={`px-2 py-0.5 text-[10px] font-bold border ${STATUS_PILL(c.status)}`} data-testid={`fi-status-${k}`}>{c.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-2">
                    {k === "1_trial_balance_vs_gl" && <>TB Dr {formatINR(c.tb_total_debit)} · TB Cr {formatINR(c.tb_total_credit)} · exact match: {String(c.exact_match)}</>}
                    {k === "2_debits_equal_credits" && <>Live Dr {formatINR(c.live_debit)} = Cr {formatINR(c.live_credit)} (diff {formatINR(c.live_difference)})</>}
                    {k === "3_voucher_type_posting" && <>{(c.rows || []).length} voucher types · {(c.rows || []).filter(r => !r.ok).length} with issues</>}
                    {k === "4_fixed_asset_register" && <>GL control {formatINR(c.gl_control_balance)} · {c.far_legacy_count_excluded} legacy assets deferred</>}
                    {k === "5_inventory" && <>Subsidiary valuation {formatINR(c.subsidiary_valuation)} ({c.batches} batches) — periodic system</>}
                    {k === "6_trade_debtors" && <>Outstanding {formatINR(c.module_reconciliation?.adapter_outstanding)} · drift rows {c.module_reconciliation?.unexplained_drift_rows}</>}
                    {k === "7_trade_creditors" && <>Trade creditors balance {formatINR(c.gl_trade_creditors_live_balance_cr)} Cr</>}
                    {k === "8_gst_tds" && <>GST Output {formatINR(c.gst_output_si_cr?.gl)} · GST Input {formatINR(c.gst_input_pi_dr?.gl)} · all source-tied</>}
                    {k === "9_bank_cash" && <>Bank/Cash net {formatINR(c.gl_bank_cash_live_net)} · all legs source-tied</>}
                    {k === "10_statements_tie_tb" && <>BS tie: {String(c.balance_sheet?.ties_to_trial_balance)} · P&L tie: {String(c.profit_loss?.ties_to_trial_balance)} · PBT {formatINR(c.profit_loss?.profit_before_tax)}</>}
                  </div>
                  <div className="text-[10px] text-orange-600 mt-2">Click for full figures & exceptions →</div>
                </Card>
              );
            })}
          </div>

          <Card className="p-4 mt-4 border-amber-200 bg-amber-50/50" data-testid="fi-exceptions-summary">
            <div className="font-bold text-[11px] uppercase tracking-wider text-amber-800 mb-2">Exception Summary — agreed-deferred (Opening Balance migration)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-700">
              <div>Orphan GL entries: <b>{out.orphan_gl_entries}</b> (net {formatINR(out.orphan_net_difference)})</div>
              <div>Legacy assets: <b>{out.legacy_assets_pending_opening_balance}</b> ({formatINR(out.legacy_assets_value)})</div>
              <div>Historic PI asset lines w/o FAR: <b>{out.historic_pi_asset_lines_without_far}</b></div>
              <div>Orphan voucher groups: <b>{out.orphan_voucher_groups}</b></div>
            </div>
          </Card>
        </>
      )}

      {tab === "history" && (
        <Card className="overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 border-b bg-slate-50">
                <th className="p-2">Certificate No.</th><th className="p-2">FY</th>
                <th className="p-2">Generated At</th><th className="p-2">Generated By</th>
                <th className="p-2">Verdict</th><th className="p-2 text-right">Live Dr = Cr</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {history.items.length === 0 && <tr><td className="p-4 text-slate-400" colSpan={7}>No certificates generated yet.</td></tr>}
              {history.items.map(h => (
                <tr key={h.id} className="border-b hover:bg-slate-50" data-testid={`fi-history-${h.certificate_number}`}>
                  <td className="p-2 font-semibold">{h.certificate_number}</td>
                  <td className="p-2">{h.fy_code}</td>
                  <td className="p-2">{fmtDT(h.generated_at)}</td>
                  <td className="p-2">{h.generated_by}</td>
                  <td className="p-2"><span className={`px-2 py-0.5 text-[10px] font-bold ${VERDICT_STYLE[h.verdict]}`}>{h.verdict}</span></td>
                  <td className="p-2 text-right tabular">{formatINR(h.totals?.live_debit)}</td>
                  <td className="p-2"><Button variant="outline" size="sm" onClick={() => openCert(h.id)} data-testid={`fi-view-cert-${h.certificate_number}`}>View</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* check drill-down */}
      <Dialog open={!!drill} onOpenChange={v => { if (!v) setDrill(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto" data-testid="fi-drill-dialog">
          <DialogHeader><DialogTitle className="text-sm">{drill && labels[drill.key]}</DialogTitle></DialogHeader>
          {drill && <CheckDetail check={drill.check} />}
        </DialogContent>
      </Dialog>

      {/* certificate view (printable) */}
      <Dialog open={!!cert} onOpenChange={v => { if (!v) setCert(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto" data-testid="fi-cert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between text-sm">
              <span>Financial Integrity Certificate</span>
              <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="fi-print-cert-btn">
                <Printer className="w-4 h-4 mr-1" /> Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          {cert && (
            <div className="border-2 border-slate-800 p-6 print:border-black" id="fi-certificate-print">
              <div className="text-center border-b-2 border-slate-800 pb-4 mb-4">
                <div className="font-display text-xl font-black tracking-wide">FINANCIAL INTEGRITY CERTIFICATE</div>
                <div className="text-xs text-slate-500 mt-1">PrimeWork ERP — issued from the verified General Ledger (single accounting source of truth)</div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs mb-4">
                <div>Certificate No.: <b data-testid="fi-cert-number">{cert.certificate_number}</b></div>
                <div>Financial Year: <b>{cert.fy_code}</b></div>
                <div>Generated At: <b>{fmtDT(cert.generated_at)}</b></div>
                <div>Generated By: <b>{cert.generated_by}</b></div>
              </div>
              <div className={`text-center py-3 mb-4 font-display text-2xl font-black ${VERDICT_STYLE[cert.verdict]}`} data-testid="fi-cert-verdict">{cert.verdict}</div>
              <table className="w-full text-xs mb-4">
                <tbody>
                  <tr className="border-b"><td className="p-1.5 text-slate-500">Live Books — Total Debits</td><td className="p-1.5 text-right tabular font-bold">{formatINR(cert.totals?.live_debit)}</td></tr>
                  <tr className="border-b"><td className="p-1.5 text-slate-500">Live Books — Total Credits</td><td className="p-1.5 text-right tabular font-bold">{formatINR(cert.totals?.live_credit)}</td></tr>
                  <tr className="border-b"><td className="p-1.5 text-slate-500">Difference</td><td className="p-1.5 text-right tabular font-bold">{formatINR(cert.totals?.live_difference)}</td></tr>
                  <tr className="border-b"><td className="p-1.5 text-slate-500">Trial Balance Dr / Cr (incl. legacy orphans)</td><td className="p-1.5 text-right tabular">{formatINR(cert.totals?.tb_total_debit)} / {formatINR(cert.totals?.tb_total_credit)}</td></tr>
                </tbody>
              </table>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">Verification Results</div>
              <table className="w-full text-xs mb-4">
                <tbody>
                  {Object.keys(cert.check_labels || {}).map(k => (
                    <tr key={k} className="border-b">
                      <td className="p-1.5">{cert.check_labels[k]}</td>
                      <td className="p-1.5 text-right"><span className={`px-2 py-0.5 text-[10px] font-bold border ${STATUS_PILL(cert.report?.[k]?.status)}`}>{cert.report?.[k]?.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">Exception Summary</div>
              <div className="text-xs text-slate-600">
                Orphan GL entries: {cert.exception_summary?.orphan_gl_entries} (net {formatINR(cert.exception_summary?.orphan_net_difference)}) ·
                Legacy assets pending opening balance: {cert.exception_summary?.legacy_assets_pending_opening_balance} ({formatINR(cert.exception_summary?.legacy_assets_value)}) ·
                Historic PI asset lines without FAR: {cert.exception_summary?.historic_pi_asset_lines_without_far}
              </div>
              <div className="text-[10px] text-slate-400 mt-4 border-t pt-2">
                System-generated from ledger_entries + jv_ledgers only. No separate calculation engine exists in PrimeWork.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
